import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyFormModal = ({ open, onClose, mode = 'create', initialValues = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    tax_state_rate: '',
    tax_city_rate: '',
    tax_county_rate: '',
    tax_tourist_rate: ''
  });

  useEffect(() => {
    if (initialValues && mode === 'edit') {
      setFormData({
        name: initialValues.name || '',
        address: initialValues.address || '',
        city: initialValues.city || '',
        state: initialValues.state || '',
        zip_code: initialValues.zip_code || '',
        phone: initialValues.phone || '',
        email: initialValues.email || '',
        tax_state_rate: initialValues.tax_state_rate || '',
        tax_city_rate: initialValues.tax_city_rate || '',
        tax_county_rate: initialValues.tax_county_rate || '',
        tax_tourist_rate: initialValues.tax_tourist_rate || ''
      });
    } else if (mode === 'create') {
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        email: '',
        tax_state_rate: '',
        tax_city_rate: '',
        tax_county_rate: '',
        tax_tourist_rate: ''
      });
    }
  }, [initialValues, mode, open]);

  const calculateTotalTax = () => {
    const state = parseFloat(formData.tax_state_rate) || 0;
    const city = parseFloat(formData.tax_city_rate) || 0;
    const county = parseFloat(formData.tax_county_rate) || 0;
    const tourist = parseFloat(formData.tax_tourist_rate) || 0;
    return (state + city + county + tourist).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('access_token');
      
      // Convert tax fields to numbers
      const submitData = {
        ...formData,
        tax_state_rate: formData.tax_state_rate ? parseFloat(formData.tax_state_rate) : null,
        tax_city_rate: formData.tax_city_rate ? parseFloat(formData.tax_city_rate) : null,
        tax_county_rate: formData.tax_county_rate ? parseFloat(formData.tax_county_rate) : null,
        tax_tourist_rate: formData.tax_tourist_rate ? parseFloat(formData.tax_tourist_rate) : null
      };
      
      if (mode === 'edit' && initialValues?.id) {
        await axios.patch(`${API}/properties/${initialValues.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Property updated successfully');
      } else {
        const response = await axios.post(`${API}/properties`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Property created successfully');
        if (onSuccess) onSuccess(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Property form error:', error.response?.data);
      toast.error(error.response?.data?.detail || `Failed to ${mode} property`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Property' : 'Add New Property'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Property Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Paradise Hotel"
                required
              />
            </div>
            <div className="col-span-2">
              <Label>Street Address</Label>
              <Input
                data-testid="property-address-input"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="123 Main Street"
              />
            </div>
            <div>
              <Label>City</Label>
              <Input
                data-testid="property-city-input"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                placeholder="Miami"
              />
            </div>
            <div>
              <Label>State</Label>
              <Input
                data-testid="property-state-input"
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                placeholder="FL"
              />
            </div>
            <div>
              <Label>Zip Code</Label>
              <Input
                data-testid="property-zip-input"
                value={formData.zip_code}
                onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                placeholder="33101"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1 234 567 8900"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="info@hotel.com"
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3">Tax Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>State Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="99.99"
                  value={formData.tax_state_rate}
                  onChange={(e) => setFormData({...formData, tax_state_rate: e.target.value})}
                  placeholder="6.00"
                />
              </div>
              <div>
                <Label>City Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="99.99"
                  value={formData.tax_city_rate}
                  onChange={(e) => setFormData({...formData, tax_city_rate: e.target.value})}
                  placeholder="2.00"
                />
              </div>
              <div>
                <Label>County Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="99.99"
                  value={formData.tax_county_rate}
                  onChange={(e) => setFormData({...formData, tax_county_rate: e.target.value})}
                  placeholder="1.00"
                />
              </div>
              <div>
                <Label>Tourist Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="99.99"
                  value={formData.tax_tourist_rate}
                  onChange={(e) => setFormData({...formData, tax_tourist_rate: e.target.value})}
                  placeholder="3.00"
                />
              </div>
            </div>
            <div className="mt-3 p-3 bg-slate-50 rounded-lg">
              <Label className="text-sm">Total Tax Rate</Label>
              <p className="text-2xl font-bold text-cyan-600">{calculateTotalTax()}%</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="submit" className="flex-1">
              {mode === 'edit' ? 'Save Changes' : 'Create Property'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyFormModal;
